import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [userAccess, setUserAccess] = useState(null);

  const fetchUserAccess = async (authToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/users/check-access`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUserAccess(response.data.access);
      return response.data.access;
    } catch (error) {
      console.error('Failed to fetch user access:', error);
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          // Set user from localStorage first for immediate UI
          setUser(JSON.parse(storedUser));
          setToken(storedToken);
          
          // Then verify with server
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          // Fetch user access permissions
          await fetchUserAccess(storedToken);
        } catch (error) {
          console.error('Token verification failed:', error);
          // Clear invalid session
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          setToken(null);
          setUserAccess(null);
        }
      }
      setLoading(false);
      setInitialized(true);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { user: userData, token: newToken } = response.data;
    setUser(userData);
    setToken(newToken);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Fetch user access permissions
    await fetchUserAccess(newToken);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    const { user: newUser, token: newToken } = response.data;
    setUser(newUser);
    setToken(newToken);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    
    // Fetch user access permissions
    await fetchUserAccess(newToken);
    
    return newUser;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setUserAccess(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };
  
  const hasAccess = (feature) => {
    if (!userAccess) return true; // Default to allow while loading
    return userAccess[feature] !== false;
  };
  
  const isAdmin = () => user?.role === 'ADMIN';
  const isWorker = () => user?.role === 'WORKER';

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, initialized, userAccess,
      login, register, logout, hasAccess, isAdmin, isWorker 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const api = axios.create({
  baseURL: `${API_URL}/api`
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);
